import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Download,
  Heart,
  Droplet,
  Users,
  Clock,
  CheckCircle,
  TrendingUp,
  Award,
  Activity,
  MapPin
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../../config/api';
import Overview from './Overview';
import Profile from './Profile';
import AvailableRequests from './AvailableRequests';
import AcceptedRequests from './AcceptedRequests';
import DonationHistory from './DonationHistory';
import DonorActivity from './DonorActivity';

const DonorDashboard = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    donor: {
      name: '',
      bloodType: '',
      lastDonation: null,
      totalDonations: 0,
      donationStreak: 0,
      nextEligibleDate: new Date().toISOString()
    },
    donationHistory: [],
    acceptedRequests: [],
    nearbyRequests: [],
    upcomingAppointments: [],
    upcomingEvents: [],
    impactMetrics: {
      totalDonations: 0,
      livesSaved: 0,
      hospitalsServed: 0,
      successRate: 0
    },
    monthlyDonations: Array(12).fill(0),
    achievements: [],
    yoyGrowth: 0
  });

  useEffect(() => {
    fetchDonorDashboard();
  }, []);

  const fetchDonorDashboard = async () => {
    try {
      setLoading(true);
      const response = await api.get('/dashboard/donor');
      setDashboardData(response.data);
      setLoading(false);
    } catch (err) {
      // If 404, it means no specific donor dashboard data exists yet (new user), so just keep defaults
      if (err.response && err.response.status === 404) {
        console.log('No donor dashboard data found (new user or profile incomplete). Using defaults.');
        setLoading(false);
        return;
      }

      console.error('Error fetching donor dashboard:', err);
      // Only show toast for non-404 errors to avoid annoying new users
      if (err.response?.status !== 404) {
        toast.error('Failed to load dashboard data');
      }
      setLoading(false);
    }
  };

  // Handle certificate download
  const handleDownloadCertificate = async (donation) => {
    try {
      // Show loading toast
      toast.info('Preparing your certificate for download...', {
        position: 'top-right',
        autoClose: 2000,
      });

      // In a real implementation, we would make an API call to get the certificate
      // For example:
      // const response = await api.get(`/donors/certificate/${donation.donationId}`, { responseType: 'blob' });

      // For now, we'll simulate a delay and then show a success message
      setTimeout(() => {
        // Success toast
        toast.success('Certificate downloaded successfully!', {
          position: 'top-right',
          autoClose: 3000,
        });

        // In a real implementation, we would create a download link for the blob
        // const url = window.URL.createObjectURL(new Blob([response.data]));
        // const link = document.createElement('a');
        // link.href = url;
        // link.setAttribute('download', `BloodHero_Certificate_${donation.donationId}.pdf`);
        // document.body.appendChild(link);
        // link.click();
        // link.remove();
      }, 1500);
    } catch (error) {
      console.error('Error downloading certificate:', error);
      toast.error('Failed to download certificate. Please try again later.', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  // Get current section based on URL
  const getCurrentSection = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'overview';
    if (path.includes('/dashboard/available')) return 'available';
    if (path.includes('/dashboard/accepted')) return 'accepted';
    if (path.includes('/dashboard/history')) return 'history';
    if (path.includes('/dashboard/profile')) return 'profile';
    if (path.includes('/dashboard/activity')) return 'activity';
    return 'overview';
  };

  // Render the appropriate content based on the current route
  const renderContent = () => {
    const section = getCurrentSection();

    switch (section) {
      case 'overview':
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Overview />
          </motion.div>
        );
      case 'available':
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <AvailableRequests nearbyRequests={dashboardData.nearbyRequests} loading={loading} />
          </motion.div>
        );
      case 'accepted':
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <AcceptedRequests acceptedRequests={dashboardData.acceptedRequests} loading={loading} />
          </motion.div>
        );
      case 'history':
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <DonationHistory
              donationHistory={dashboardData.donationHistory}
              loading={loading}
              handleDownloadCertificate={handleDownloadCertificate}
            />
          </motion.div>
        );
      case 'profile':
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Profile donor={dashboardData.donor} loading={loading} />
          </motion.div>
        );
      case 'activity':
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <DonorActivity
              dashboardData={dashboardData}
              loading={loading}
            />
          </motion.div>
        );
      default:
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Overview />
          </motion.div>
        );
    }
  };

  // Render dashboard header for overview section
  const renderDashboardHeader = () => {
    const section = getCurrentSection();
    if (section !== 'overview') return null;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-8"
      >
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-3xl shadow-xl p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white mb-1">
                  Donor Dashboard
                </h1>
                <p className="text-red-100 text-sm">
                  Welcome back, {dashboardData.donor.name || 'Hero'}! Keep saving lives
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-3 py-1.5 flex items-center space-x-2">
                <Droplet className="w-4 h-4 text-red-200" />
                <span className="text-white text-sm font-semibold">{dashboardData.donor.bloodType}</span>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-3 py-1.5 flex items-center space-x-2">
                <Award className="w-4 h-4 text-red-200" />
                <span className="text-white text-sm font-semibold">{dashboardData.donor.totalDonations} Donations</span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl p-4 border border-red-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-600 text-sm font-medium">Total Donations</p>
                <p className="text-xl font-bold text-gray-900">{dashboardData.impactMetrics.totalDonations}</p>
              </div>
              <div className="w-10 h-10 bg-red-500 rounded-xl flex items-center justify-center">
                <Heart className="w-5 h-5 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-4 border border-green-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-600 text-sm font-medium">Lives Saved</p>
                <p className="text-xl font-bold text-gray-900">{dashboardData.impactMetrics.livesSaved}</p>
              </div>
              <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center">
                <Users className="w-5 h-5 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-4 border border-blue-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-600 text-sm font-medium">Hospitals Served</p>
                <p className="text-xl font-bold text-gray-900">{dashboardData.impactMetrics.hospitalsServed}</p>
              </div>
              <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center">
                <MapPin className="w-5 h-5 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-4 border border-purple-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-600 text-sm font-medium">Success Rate</p>
                <p className="text-xl font-bold text-gray-900">{dashboardData.impactMetrics.successRate}%</p>
              </div>
              <div className="w-10 h-10 bg-purple-500 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Main Content Container */}
      <div className="relative pt-6 md:pt-8 px-6 pb-6">
        {renderDashboardHeader()}
        <AnimatePresence mode="wait">
          {renderContent()}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default DonorDashboard;